import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Steps, 
  Button, 
  Upload, 
  message, 
  Typography, 
  Descriptions, 
  Table, 
  Select, 
  Alert,
  Space,
  Result,
  Spin,
  Checkbox,
  Tabs,
  Badge
} from 'antd';
import { 
  UploadOutlined, 
  FileOutlined, 
  CheckCircleOutlined, 
  ExclamationCircleOutlined 
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { Customer } from '../types.ts';
import apiClient from '../utils/axiosConfig.ts';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// Helper function to generate composite identifier matching backend logic
const generateCompositeIdentifier = (internalCode: string, size?: string | null, color?: string | null): string => {
  const sizePart = (size || '').trim();
  const colorPart = (color || '').trim();
  
  if (!sizePart && !colorPart) {
    return internalCode; // ABC123
  } else if (sizePart && !colorPart) {
    return `${internalCode}-${sizePart}`; // ABC123-L
  } else if (!sizePart && colorPart) {
    return `${internalCode}--${colorPart}`; // ABC123--RED
  } else {
    return `${internalCode}-${sizePart}-${colorPart}`; // ABC123-L-RED
  }
};

interface ProductImportModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

interface ImportData {
  file_type: string;
  status: string;
  message: string;
  total_products: number;
  products: any[];
  preview: any;
  mapping: any;
}

interface ValidationResult {
  total_products: number;
  valid_products: number;
  errors: any[];
  warnings: any[];
  can_proceed: boolean;
}

interface ImportResult {
  success: boolean;
  imported_count: number;
  updated_count: number;
  skipped_count: number;
  error_count: number;
  imported_products: any[];
  updated_products: any[];
  skipped_products: any[];
  errors: any[];
}

const ProductImportModal: React.FC<ProductImportModalProps> = ({
  visible,
  onCancel,
  onSuccess
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importData, setImportData] = useState<ImportData | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<number | undefined>(undefined);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [overwriteExisting, setOverwriteExisting] = useState<boolean>(false);

  // Carica clienti quando si raggiunge la fase 2
  useEffect(() => {
    if (currentStep === 2) {
      loadCustomers();
    }
  }, [currentStep]);

  // Reset stato quando si chiude la modal
  const handleCancel = () => {
    setCurrentStep(0);
    setFileList([]);
    setImportData(null);
    setValidationResult(null);
    setSelectedCustomer(undefined);
    setImportResult(null);
    setOverwriteExisting(false);
    onCancel();
  };

  // Gestione upload file
  const uploadProps: UploadProps = {
    name: 'file',
    accept: '.xml,.ods,.xls,.xlsx',
    beforeUpload: () => false, // Previeni upload automatico
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    fileList,
  };

  // Fase 1: Upload e parsing file
  const handleFileUpload = async () => {
    if (fileList.length === 0) {
      message.error('Seleziona un file da importare');
      return;
    }

    const file = fileList[0].originFileObj;
    if (!file) return;

    console.log('=== PRODUCT IMPORT UPLOAD DEBUG ===');
    console.log('File selected:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });

    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      console.log('FormData created:', formData);
      console.log('Token present:', !!localStorage.getItem('token'));

      console.log('Making request to: /product-import/upload');
      const response = await apiClient.post('/product-import/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Response received:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });

      const data: ImportData = response.data;
      console.log('Upload success:', data);
      setImportData(data);

      if (data.status === 'not_implemented') {
        message.warning(data.message);
        setCurrentStep(4); // Vai direttamente alla fine
      } else if (data.status === 'success') {
        message.success(data.message);
        setCurrentStep(1);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Errore durante il parsing del file';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fase 2: Validazione e anteprima
  const handleValidation = async () => {
    if (!importData?.products) return;

    setLoading(true);
    
    try {
      const response = await apiClient.post('/product-import/validate', { 
        products: importData.products 
      });

      const result: ValidationResult = response.data;
      setValidationResult(result);

      if (result.can_proceed) {
        setCurrentStep(2);
      } else {
        console.error('Validation errors:', result.errors);
        // Invece di un messaggio generico, andiamo alla fase di visualizzazione errori
        setCurrentStep(1.5); // Stato intermedio per mostrare errori
      }
    } catch (error: any) {
      console.error('Validation error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Errore durante la validazione';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Carica lista clienti per fase associazione
  const loadCustomers = async () => {
    try {
      const response = await apiClient.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
    }
  };

  // Fase 3: Associazione cliente (opzionale)
  const handleCustomerAssociation = () => {
    setCurrentStep(3);
  };

  // Fase 4: Esecuzione import
  const handleImportExecution = async () => {
    if (!importData?.products) return;

    // Se abbiamo errori di validazione, usa solo i prodotti validi
    let productsToImport = importData.products;
    if (validationResult && validationResult.errors.length > 0) {
      // Filtra i prodotti escludendo quelli con errori
      const errorProductCodes = new Set(validationResult.errors.map(error => error.product_code));
      productsToImport = importData.products.filter(product => 
        !errorProductCodes.has(product.internal_product_code)
      );
    }

    setLoading(true);
    
    try {
      const response = await apiClient.post('/product-import/execute', { 
        products: productsToImport,
        customer_id: selectedCustomer,
        overwrite_existing: overwriteExisting
      });

      const result: ImportResult = response.data;
      setImportResult(result);

      if (result.success) {
        const messages = [];
        if (result.imported_count > 0) messages.push(`${result.imported_count} nuovi prodotti`);
        if (result.updated_count > 0) messages.push(`${result.updated_count} prodotti aggiornati`);
        if (result.skipped_count > 0) messages.push(`${result.skipped_count} prodotti saltati`);
        
        message.success(`Importazione completata: ${messages.join(', ')}`);
        onSuccess();
      } else {
        console.error('Import errors:', result.errors);
        message.error(`Importazione completata con ${result.error_count} errori`);
      }
      
      setCurrentStep(4);
    } catch (error: any) {
      console.error('Import execution error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Errore durante l\'importazione';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Naviga tra le fasi
  const goToStep = (step: number) => {
    // Considera step intermedi come 1.5
    if (step <= Math.floor(currentStep) || step === 0) {
      setCurrentStep(step);
    }
  };

  // Definizione dei passi
  const steps = [
    {
      title: 'Seleziona File',
      icon: <UploadOutlined />,
    },
    {
      title: 'Anteprima e Mappatura', 
      icon: <FileOutlined />,
    },
    {
      title: 'Associazione Cliente',
      icon: <CheckCircleOutlined />,
    },
    {
      title: 'Esecuzione',
      icon: <ExclamationCircleOutlined />,
    },
  ];

  // Rendering contenuto per ogni fase
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Upload.Dragger {...uploadProps} style={{ marginBottom: 24 }}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Clicca o trascina il file in quest'area per caricarlo</p>
              <p className="ant-upload-hint">
                Supportati: file XML, ODS, XLS, XLSX
              </p>
            </Upload.Dragger>
            
            <Alert
              message="Template Supportati"
              description={
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Template XML:</strong> Formato esistente con mappatura OGGETTO, DOGGETTO, TAGLIA, COLORE, FORNITORE
                  </div>
                  <div>
                    <strong>Template ODS / XLSX / XLS:</strong> Colonne A="code"→cliente, B="description", C="NS CODICE", D="CODICE DOGANALE", E="Prezzo"
                  </div>
                </div>
              }
              type="info"
              style={{ marginBottom: 24 }}
            />
            
            <div style={{ marginBottom: 24 }}>
              <Checkbox 
                checked={overwriteExisting}
                onChange={(e) => setOverwriteExisting(e.target.checked)}
              >
                Sovrascrivi prodotti esistenti
              </Checkbox>
              <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                Se attivo, i prodotti già presenti nel database verranno aggiornati invece di essere saltati
              </div>
            </div>
            
            <Space>
              <Button 
                type="primary" 
                onClick={handleFileUpload}
                loading={loading}
                disabled={fileList.length === 0}
              >
                Analizza File
              </Button>
            </Space>
          </div>
        );

      case 1:
        if (!importData) return null;
        
        return (
          <div>
            <Alert
              message={`File parsato con successo: ${importData.total_products} prodotti trovati`}
              type="success"
              style={{ marginBottom: 24 }}
            />
            
            {importData.preview && (
              <>
                <Title level={4}>Anteprima Primo Prodotto:</Title>
                <Descriptions bordered column={1} style={{ marginBottom: 24 }}>
                  <Descriptions.Item label="Codice Interno">
                    {importData.preview.internal_product_code}
                  </Descriptions.Item>
                  <Descriptions.Item label="Descrizione">
                    {importData.preview.product_description}
                  </Descriptions.Item>
                  <Descriptions.Item label="Taglia">
                    {importData.preview.size || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Colore">
                    {importData.preview.color || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Codice Cliente">
                    {importData.preview.client_product_code || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Codice Fornitore">
                    {importData.preview.supplier_product_code || importData.preview.supplier_code || 'N/A'}
                  </Descriptions.Item>
                  {importData.preview.dogana_code && (
                    <Descriptions.Item label="Codice Dogana">
                      {importData.preview.dogana_code}
                    </Descriptions.Item>
                  )}
                  {importData.preview.price && (
                    <Descriptions.Item label="Vendita">
                      €{importData.preview.price}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}

            <Title level={4}>Mappatura Campi:</Title>
            <Table
              dataSource={Object.entries(importData.mapping).map(([key, value], index) => ({
                key: index,
                campo_source: key,
                campo_db: value
              }))}
              columns={[
                { 
                  title: importData.file_type === '.xml' ? 'Campo XML' : 'Campo File', 
                  dataIndex: 'campo_source', 
                  key: 'campo_source' 
                },
                { title: 'Campo Database', dataIndex: 'campo_db', key: 'campo_db' }
              ]}
              pagination={false}
              size="small"
            />

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Button type="primary" onClick={handleValidation} loading={loading}>
                Conferma Mappatura e Procedi
              </Button>
            </div>
          </div>
        );

      case 1.5:
        if (!validationResult) return null;
        
        return (
          <div>
            <Alert
              message={`Validazione completata: ${validationResult.valid_products} prodotti validi, ${validationResult.errors.length} errori`}
              type="warning"
              style={{ marginBottom: 24 }}
            />

            {validationResult.errors.length > 0 && (
              <>
                <Title level={4}>Errori Trovati:</Title>
                <Table
                  dataSource={validationResult.errors.map((error, index) => ({
                    key: index,
                    row: error.row,
                    product_code: error.product_code,
                    errors: error.errors.join(', ')
                  }))}
                  columns={[
                    { title: 'Riga', dataIndex: 'row', key: 'row', width: 80 },
                    { title: 'Codice Prodotto', dataIndex: 'product_code', key: 'product_code', width: 150 },
                    { title: 'Errori', dataIndex: 'errors', key: 'errors' }
                  ]}
                  pagination={{ pageSize: 10 }}
                  size="small"
                  style={{ marginBottom: 24 }}
                />
              </>
            )}

            {validationResult.warnings.length > 0 && (
              <>
                <Title level={4}>Avvisi:</Title>
                <Alert
                  message="Avvisi"
                  description={
                    <ul>
                      {validationResult.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  }
                  type="info"
                  style={{ marginBottom: 24 }}
                />
              </>
            )}

            <Space>
              <Button onClick={() => setCurrentStep(1)}>
                Indietro
              </Button>
              {validationResult.valid_products > 0 && (
                <Button 
                  type="primary" 
                  onClick={() => setCurrentStep(2)}
                >
                  Procedi con {validationResult.valid_products} prodotti validi
                </Button>
              )}
            </Space>
          </div>
        );

      case 2:
        return (
          <div>
            <Title level={4}>Associazione Cliente (Opzionale)</Title>
            <Paragraph>
              Seleziona un cliente per associare tutti i prodotti importati. 
              Questa fase è opzionale e può essere saltata.
            </Paragraph>

            <Select
              style={{ width: '100%', marginBottom: 24 }}
              placeholder="Seleziona un cliente (opzionale)"
              value={selectedCustomer}
              onChange={setSelectedCustomer}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {customers.map(customer => (
                <Option key={customer.id} value={customer.id}>
                  {customer.company}
                </Option>
              ))}
            </Select>

            <Space>
              <Button onClick={() => setCurrentStep(1)}>
                Indietro
              </Button>
              <Button type="primary" onClick={handleCustomerAssociation}>
                Procedi all'Importazione
              </Button>
            </Space>
          </div>
        );

      case 3:
        if (!validationResult) return null;

        return (
          <div>
            <Title level={4}>Conferma Importazione</Title>
            
            <Alert
              message={`Pronti per importare ${validationResult.valid_products} prodotti`}
              type="info"
              style={{ marginBottom: 24 }}
            />

            {validationResult.warnings.length > 0 && (
              <Alert
                message="Avvisi"
                description={
                  <ul>
                    {validationResult.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                }
                type="warning"
                style={{ marginBottom: 24 }}
              />
            )}

            {selectedCustomer && (
              <Alert
                message={`I prodotti verranno associati al cliente selezionato`}
                type="success"
                style={{ marginBottom: 24 }}
              />
            )}

            <Space>
              <Button onClick={() => setCurrentStep(2)}>
                Indietro
              </Button>
              <Button 
                type="primary" 
                onClick={handleImportExecution}
                loading={loading}
              >
                Esegui Importazione
              </Button>
            </Space>
          </div>
        );

      case 4:
        if (importData?.status === 'not_implemented') {
          return (
            <Result
              icon={<ExclamationCircleOutlined />}
              title="Funzionalità in Sviluppo"
              subTitle="L'importazione di file XLS/XLSX è ancora in fase di sviluppo"
              extra={
                <Button type="primary" onClick={handleCancel}>
                  Chiudi
                </Button>
              }
            />
          );
        }

        if (importResult) {
          const totalProcessed = importResult.imported_count + importResult.updated_count + importResult.skipped_count;
          
          // Preparazione dei tabs con badge
          const tabItems = [
            {
              key: 'imported',
              label: (
                <Badge count={importResult.imported_count} showZero>
                  <span style={{ marginRight: 8 }}>Nuovi Prodotti</span>
                </Badge>
              ),
              children: (
                <Table
                  dataSource={importResult.imported_products.map((product, index) => ({
                    key: index,
                    id: product.id,
                    internal_product_code: product.internal_product_code,
                    composite_identifier: product.composite_identifier || generateCompositeIdentifier(product.internal_product_code, product.size, product.color),
                    product_description: product.product_description,
                    size: product.size,
                    color: product.color
                  }))}
                  columns={[
                    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
                    { title: 'Codice Base', dataIndex: 'internal_product_code', key: 'internal_product_code', width: 120 },
                    { title: 'Identificatore Completo', dataIndex: 'composite_identifier', key: 'composite_identifier', width: 180 },
                    { title: 'Taglia', dataIndex: 'size', key: 'size', width: 80 },
                    { title: 'Colore', dataIndex: 'color', key: 'color', width: 80 },
                    { title: 'Descrizione', dataIndex: 'product_description', key: 'product_description' }
                  ]}
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              )
            },
            {
              key: 'updated',
              label: (
                <Badge count={importResult.updated_count} showZero>
                  <span style={{ marginRight: 8 }}>Prodotti Aggiornati</span>
                </Badge>
              ),
              children: (
                <Table
                  dataSource={importResult.updated_products.map((product, index) => ({
                    key: index,
                    id: product.id,
                    internal_product_code: product.internal_product_code,
                    composite_identifier: product.composite_identifier || generateCompositeIdentifier(product.internal_product_code, product.size, product.color),
                    product_description: product.product_description,
                    size: product.size,
                    color: product.color
                  }))}
                  columns={[
                    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
                    { title: 'Codice Base', dataIndex: 'internal_product_code', key: 'internal_product_code', width: 120 },
                    { title: 'Identificatore Completo', dataIndex: 'composite_identifier', key: 'composite_identifier', width: 180 },
                    { title: 'Taglia', dataIndex: 'size', key: 'size', width: 80 },
                    { title: 'Colore', dataIndex: 'color', key: 'color', width: 80 },
                    { title: 'Descrizione', dataIndex: 'product_description', key: 'product_description' }
                  ]}
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              )
            },
            {
              key: 'skipped',
              label: (
                <Badge count={importResult.skipped_count} showZero>
                  <span style={{ marginRight: 8 }}>Prodotti Saltati</span>
                </Badge>
              ),
              children: (
                <Table
                  dataSource={importResult.skipped_products.map((product, index) => ({
                    key: index,
                    internal_product_code: product.internal_product_code,
                    composite_identifier: product.composite_identifier || generateCompositeIdentifier(product.internal_product_code, product.size, product.color),
                    product_description: product.product_description,
                    size: product.size,
                    color: product.color
                  }))}
                  columns={[
                    { title: 'Codice Base', dataIndex: 'internal_product_code', key: 'internal_product_code', width: 120 },
                    { title: 'Identificatore Completo', dataIndex: 'composite_identifier', key: 'composite_identifier', width: 180 },
                    { title: 'Taglia', dataIndex: 'size', key: 'size', width: 80 },
                    { title: 'Colore', dataIndex: 'color', key: 'color', width: 80 },
                    { title: 'Descrizione', dataIndex: 'product_description', key: 'product_description' },
                    { title: 'Motivo', render: () => 'Prodotto già esistente (stesso codice, taglia e colore)', key: 'reason', width: 200 }
                  ]}
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              )
            },
            {
              key: 'errors',
              label: (
                <Badge count={importResult.error_count} showZero>
                  <span style={{ marginRight: 8 }}>Errori</span>
                </Badge>
              ),
              children: (
                <Table
                  dataSource={importResult.errors.map((error, index) => ({
                    key: index,
                    row: error.row,
                    product_code: error.product_code,
                    error: error.error
                  }))}
                  columns={[
                    { title: 'Riga', dataIndex: 'row', key: 'row', width: 80 },
                    { title: 'Codice Prodotto', dataIndex: 'product_code', key: 'product_code', width: 150 },
                    { title: 'Errore', dataIndex: 'error', key: 'error' }
                  ]}
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              )
            }
          ];

          return (
            <div>
              {/* Header con riassunto */}
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: 8 }}>
                  {importResult.success ? (
                    <span style={{ color: '#52c41a' }}>✅ Importazione Completata</span>
                  ) : (
                    <span style={{ color: '#faad14' }}>⚠️ Importazione Completata con {importResult.error_count} Errori</span>
                  )}
                </div>
                <div style={{ color: '#666' }}>
                  {totalProcessed} prodotti processati totali
                </div>
              </div>

              {/* Tabs con dettagli */}
              <Tabs
                defaultActiveKey={importResult.imported_count > 0 ? 'imported' : 'errors'}
                items={tabItems}
                style={{ marginBottom: 24 }}
              />

              {/* Pulsanti azione */}
              <div style={{ textAlign: 'center' }}>
                <Button type="primary" onClick={handleCancel}>
                  Chiudi
                </Button>
              </div>
            </div>
          );
        }

        return null;

      default:
        return null;
    }
  };

  return (
    <Modal
      title="Importazione Prodotti"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Steps 
          current={Math.floor(currentStep)} 
          items={steps}
          style={{ marginBottom: 32 }}
          onChange={goToStep}
        />
        
        {renderStepContent()}
      </Spin>
    </Modal>
  );
};

export default ProductImportModal;